import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Target, Award, TrendingUp } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import AppShell from "@/components/AppShell";
import { api_listMySessions, api_getMyProfile } from "@/lib/api";

const SKILL_LABELS = {
  putting: "Putting",
  wedges: "Wedges",
  iron: "Iron",
  driver: "Driver",
  short_game: "Short Game",
  woods: "Woods",
};
const SKILL_COLORS = {
  putting: "#0B2545",
  wedges: "#1E40AF",
  iron: "#3B82F6",
  driver: "#FACC15",
  short_game: "#10B981",
  woods: "#7C3AED",
};

export default function Stats() {
  const [sessions, setSessions] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ss, p] = await Promise.all([api_listMySessions(), api_getMyProfile()]);
        setSessions(ss);
        setProfile(p);
      } catch {
        toast.error("Could not load stats.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const completed = useMemo(() => sessions.filter((s) => s.status === "completed"), [sessions]);

  const aggregated = useMemo(() => {
    const bySkill = {};
    const byDrill = {};
    let totalShots = 0;
    let totalSuccess = 0;
    let totalMixed = 0;
    let totalFailed = 0;

    for (const s of completed) {
      for (const d of s.drills) {
        const outcomes = s.feedback?.[d.id] || [];
        if (outcomes.length === 0) continue;
        const succ = outcomes.filter((o) => o === "success").length;
        const mix = outcomes.filter((o) => o === "mixed").length;
        const fail = outcomes.filter((o) => o === "failed").length;

        totalShots += outcomes.length;
        totalSuccess += succ;
        totalMixed += mix;
        totalFailed += fail;

        const sk = d.skill || "iron";
        if (!bySkill[sk]) bySkill[sk] = { skill: sk, shots: 0, success: 0, mixed: 0, failed: 0 };
        bySkill[sk].shots += outcomes.length;
        bySkill[sk].success += succ;
        bySkill[sk].mixed += mix;
        bySkill[sk].failed += fail;

        const key = d.title;
        if (!byDrill[key]) byDrill[key] = { title: d.title, skill: sk, attempts: 0, shots: 0, success: 0, mixed: 0, failed: 0, history: [] };
        byDrill[key].attempts += 1;
        byDrill[key].shots += outcomes.length;
        byDrill[key].success += succ;
        byDrill[key].mixed += mix;
        byDrill[key].failed += fail;
        byDrill[key].history.push({ date: s.completed_at || s.created_at, rate: outcomes.length ? succ / outcomes.length : 0 });
      }
    }

    const skillPie = Object.values(bySkill).map((x) => ({
      name: SKILL_LABELS[x.skill] || x.skill,
      value: x.shots,
      key: x.skill,
      successRate: x.shots ? Math.round((x.success / x.shots) * 100) : 0,
    }));
    const skillBar = Object.values(bySkill).map((x) => ({
      skill: SKILL_LABELS[x.skill] || x.skill,
      key: x.skill,
      rate: x.shots ? Math.round((x.success / x.shots) * 100) : 0,
    }));
    const drillRows = Object.values(byDrill).sort((a, b) => b.attempts - a.attempts);

    return {
      skillPie,
      skillBar,
      drillRows,
      overall: {
        shots: totalShots,
        success: totalSuccess,
        mixed: totalMixed,
        failed: totalFailed,
        rate: totalShots ? Math.round((totalSuccess / totalShots) * 100) : 0,
      },
    };
  }, [completed]);

  if (loading) {
    return <AppShell profile={profile}><div className="text-center py-20 text-slate-500">Crunching your numbers…</div></AppShell>;
  }

  if (completed.length === 0) {
    return (
      <AppShell profile={profile}>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Stats</p>
        <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Your numbers</h1>
        <div className="mt-8 bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-10 text-center" data-testid="stats-empty">
          <BarChart3 className="w-8 h-8 text-[#0B2545] mx-auto" />
          <p className="mt-3 font-heading font-bold text-lg">No data yet</p>
          <p className="text-sm text-slate-600 mt-1">Finish at least one session to start seeing your breakdown.</p>
        </div>
      </AppShell>
    );
  }

  const { skillPie, skillBar, drillRows, overall } = aggregated;

  return (
    <AppShell profile={profile}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Stats</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Your numbers</h1>
      <p className="mt-2 text-slate-600">Every shot you've logged, sliced by skill and drill.</p>

      {/* Overall stats */}
      <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="stat-rate" icon={<Target className="w-5 h-5" />} label="Hit rate" value={`${overall.rate}%`} tone="brand" />
        <StatCard testid="stat-shots" icon={<BarChart3 className="w-5 h-5" />} label="Total shots" value={overall.shots} />
        <StatCard testid="stat-sessions" icon={<Award className="w-5 h-5" />} label="Sessions" value={completed.length} />
        <StatCard testid="stat-streak" icon={<TrendingUp className="w-5 h-5" />} label="Streak" value={`${profile?.streak_count || 0}d`} />
      </section>

      {/* Charts */}
      <section className="mt-8 grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Shots by skill</p>
          <h3 className="mt-1 font-heading font-black text-xl">Where you practice</h3>
          <div className="mt-4 h-64" data-testid="stats-pie">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={skillPie}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={3}
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {skillPie.map((entry) => (
                    <Cell key={entry.key} fill={SKILL_COLORS[entry.key] || "#94A3B8"} />
                  ))}
                </Pie>
                <ReTooltip
                  formatter={(value, name, props) => [`${value} shots (${props.payload.successRate}% hit)`, name]}
                  contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontWeight: 600 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs font-bold text-slate-700">
            {skillPie.map((s) => (
              <li key={s.key} className="inline-flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: SKILL_COLORS[s.key] || "#94A3B8" }} />
                {s.name} · {s.value}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Success rate by skill</p>
          <h3 className="mt-1 font-heading font-black text-xl">Where you're sharpest</h3>
          <div className="mt-4 h-64" data-testid="stats-bar">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={skillBar} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="skill" tick={{ fontSize: 11, fontWeight: 700, fill: "#475569" }} axisLine={false} tickLine={false} />
                <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={36} />
                <ReTooltip formatter={(v) => [`${v}%`, "Hit rate"]} cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontWeight: 600 }} />
                <Bar dataKey="rate" radius={[8, 8, 0, 0]}>
                  {skillBar.map((entry) => (
                    <Cell key={entry.key} fill={SKILL_COLORS[entry.key] || "#94A3B8"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Per-drill history */}
      <section className="mt-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Drill leaderboard</p>
        <h3 className="mt-1 font-heading font-black text-2xl">Every drill you've done</h3>
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
          {drillRows.map((d) => {
            const rate = d.shots ? Math.round((d.success / d.shots) * 100) : 0;
            return (
              <div key={d.title} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`drill-row-${d.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: SKILL_COLORS[d.skill] || "#94A3B8" }} />
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{SKILL_LABELS[d.skill] || d.skill}</span>
                  </div>
                  <p className="mt-1 font-heading font-bold text-lg leading-tight truncate">{d.title}</p>
                  <div className="mt-2 flex gap-3 text-xs font-bold text-slate-600">
                    <span>{d.attempts} {d.attempts === 1 ? "attempt" : "attempts"}</span>
                    <span>{d.shots} shots</span>
                    <span className="text-emerald-700">✓ {d.success}</span>
                    <span className="text-amber-700">~ {d.mixed}</span>
                    <span className="text-red-700">✗ {d.failed}</span>
                  </div>
                </div>
                <div className="sm:w-64 flex items-center gap-3">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${rate}%`, background: SKILL_COLORS[d.skill] || "#94A3B8" }} />
                  </div>
                  <span className="text-2xl font-heading font-black tabular-nums w-14 text-right">{rate}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </AppShell>
  );
}

function StatCard({ testid, icon, label, value, tone }) {
  const brand = tone === "brand";
  return (
    <div
      className={`rounded-2xl p-5 border ${brand ? "bg-[#0B2545] text-white border-[#0B2545]" : "bg-white border-slate-200"}`}
      data-testid={testid}
    >
      <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] ${brand ? "text-white/70" : "text-slate-500"}`}>
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-3 text-3xl sm:text-4xl font-heading font-black tabular-nums">{value}</p>
    </div>
  );
}
